import {
  Calendar,
  DollarSign,
  FileText,
  Flame,
  Hash,
  PenLine,
  ShieldCheck,
  Smartphone,
  Sparkles,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { apiPost } from '../api/client.js';
import { AIBlocks, type AIBlock } from '../components/AIBlocks.js';
import { Button } from '../components/Button.js';
import { Card } from '../components/Card.js';
import { Input } from '../components/Input.js';
import { PageHeader } from '../components/PageHeader.js';
import { Skeleton } from '../components/Skeleton.js';
import { useStores } from '../state/store-context.js';

const KINDS: { key: Kind; label: string; icon: LucideIcon }[] = [
  { key: 'title', label: 'Listing titles', icon: PenLine },
  { key: 'description', label: 'Description', icon: FileText },
  { key: 'keywords', label: 'Keywords & tags', icon: Hash },
  { key: 'pricing', label: 'Pricing advice', icon: DollarSign },
];

type Kind = 'title' | 'description' | 'keywords' | 'pricing';

const QUICK: { icon: LucideIcon; title: string; sub: string; prompt: string }[] = [
  {
    icon: Flame,
    title: 'Hot — wardrobe',
    sub: 'Top 10 right now',
    prompt:
      'Find 10 hot selling products for my young_boy_wardrobe eBay Canada store right now — show sell-through rate, avg price and profit per sale for each',
  },
  {
    icon: Smartphone,
    title: 'Hot — tech',
    sub: 'Top 10 right now',
    prompt:
      'Find 10 hot selling products for my techorbitcanada eBay Canada store right now — show sell-through rate, avg price and profit per sale for each',
  },
  {
    icon: ShieldCheck,
    title: 'Avoid mistakes',
    sub: 'Protect my stores',
    prompt:
      'What are the 5 most common mistakes eBay dropshippers in Canada make and how do I avoid them?',
  },
  {
    icon: Calendar,
    title: 'Weekly routine',
    sub: 'Minimum time',
    prompt:
      'Give me a minimum-time weekly routine for managing two eBay dropshipping stores in Canada — what to check daily and weekly',
  },
];

interface Values {
  product: string;
}

export function AIToolsPage(): JSX.Element {
  const { selectedStoreId, stores } = useStores();
  const [kind, setKind] = useState<Kind>('title');
  const { register, handleSubmit } = useForm<Values>({
    defaultValues: { product: 'Wireless earbuds TWS bluetooth' },
  });
  const [blocks, setBlocks] = useState<AIBlock[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const friendly = (e: unknown): string => {
    const msg = e instanceof Error ? e.message : String(e);
    return msg.includes('No AI provider configured')
      ? 'AI is not configured yet. Open Admin and pick a provider and paste your API key.'
      : msg;
  };

  const storeName =
    selectedStoreId === 'all'
      ? 'both stores'
      : (stores.find((s) => s.id === selectedStoreId)?.storeName ?? 'both stores');

  const generate = async (v: Values): Promise<void> => {
    setLoading(true);
    setError(null);
    setBlocks(null);
    try {
      const res = await apiPost<{ blocks: AIBlock[] }>('/api/ai/tools', {
        kind,
        product: v.product,
        store: storeName,
      });
      setBlocks(res.blocks);
    } catch (e) {
      setError(friendly(e));
    } finally {
      setLoading(false);
    }
  };

  const ask = async (prompt: string): Promise<void> => {
    setLoading(true);
    setError(null);
    setBlocks(null);
    try {
      const res = await apiPost<{ blocks: AIBlock[] }>('/api/ai/ask', { prompt, store: storeName });
      setBlocks(res.blocks);
    } catch (e) {
      setError(friendly(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PageHeader
        title="AI"
        accent="tools"
        sub="One-shot copy and pricing helpers. Quick prompts for the questions you ask most."
      />

      <Card title="Generator">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: 8,
            marginBottom: 14,
          }}
        >
          {KINDS.map((k) => {
            const active = kind === k.key;
            return (
              <button
                key={k.key}
                type="button"
                onClick={() => setKind(k.key)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  background: active ? 'var(--c-primary-soft)' : 'var(--c-card)',
                  border: '1px solid',
                  borderColor: active ? 'var(--c-primary-soft-border)' : 'var(--c-border-strong)',
                  borderRadius: 'var(--radius-md)',
                  padding: '10px 12px',
                  fontSize: 'var(--text-md)',
                  fontWeight: active ? 600 : 500,
                  color: active ? 'var(--c-primary)' : 'var(--c-text-secondary)',
                  cursor: 'pointer',
                }}
              >
                <k.icon size={15} strokeWidth={2.2} />
                {k.label}
              </button>
            );
          })}
        </div>
        <form onSubmit={handleSubmit(generate)} style={{ display: 'flex', gap: 10 }}>
          <Input
            {...register('product')}
            aria-label="Product"
            placeholder="Product name"
            style={{ flex: 1 }}
          />
          <Button type="submit" icon={Sparkles} loading={loading}>
            Generate
          </Button>
        </form>
      </Card>

      <Card title="AI output">
        {loading && <Skeleton height={140} />}
        {error && (
          <div
            style={{
              padding: 12,
              borderRadius: 'var(--radius-md)',
              background: 'var(--c-danger-bg)',
              color: 'var(--c-danger)',
              fontSize: 'var(--text-md)',
            }}
          >
            {error}
          </div>
        )}
        {blocks ? (
          <AIBlocks blocks={blocks} />
        ) : (
          !loading &&
          !error && (
            <p style={{ fontSize: 'var(--text-md)', color: 'var(--c-muted)' }}>
              Pick a type, enter your product, click Generate.
            </p>
          )
        )}
      </Card>

      <Card title="Quick prompts" description="One-click asks for common questions.">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 10,
          }}
        >
          {QUICK.map((q) => (
            <button
              key={q.title}
              type="button"
              onClick={() => ask(q.prompt)}
              style={{
                background: 'var(--c-card)',
                border: '1px solid var(--c-border)',
                borderRadius: 'var(--radius-lg)',
                padding: 14,
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--c-primary-soft)',
                  color: 'var(--c-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <q.icon size={16} strokeWidth={2.2} />
              </div>
              <div style={{ fontSize: 'var(--text-md)', fontWeight: 600 }}>{q.title}</div>
              <div style={{ fontSize: 'var(--text-base)', color: 'var(--c-muted)' }}>{q.sub}</div>
            </button>
          ))}
        </div>
      </Card>
    </div>
  );
}
